import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { gsap } from '@/lib/gsap'
import { PILLARS } from '@/constants/aerive'

// Open sky: the brand pillars rise out of blur over the fixed dusk sky
const Ascent: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)

    useEffect(() => {
        const section = sectionRef.current
        if (!section) return

        const scrim = document.querySelector<HTMLElement>('[data-sky="scrim"]')
        const mm = gsap.matchMedia()

        mm.add({
            motion: '(prefers-reduced-motion: no-preference)',
            reduce: '(prefers-reduced-motion: reduce)'
        }, (context) => {
            const { motion } = context.conditions as { motion: boolean }

            gsap.fromTo(scrim, { autoAlpha: 0 }, {
                autoAlpha: 1,
                ease: 'none',
                scrollTrigger: {
                    trigger: section,
                    start: 'top bottom',
                    end: 'top 35%',
                    scrub: true,
                    refreshPriority: 9
                }
            })

            section.querySelectorAll<HTMLElement>('[data-reveal]').forEach((element) => {
                gsap.fromTo(
                    element,
                    motion ? { autoAlpha: 0, y: 70, filter: 'blur(10px)' } : { autoAlpha: 0 },
                    {
                        autoAlpha: 1,
                        y: 0,
                        filter: 'blur(0px)',
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: element,
                            start: 'top 92%',
                            end: 'top 58%',
                            scrub: 1,
                            refreshPriority: 9
                        }
                    }
                )
            })
        })

        return () => mm.revert()
    }, [])

    return (
        <section
            id='about'
            ref={sectionRef}
            aria-labelledby='about-title'
            className='relative z-10 px-5 pb-24 pt-28 md:px-[6vw] md:pb-36 md:pt-40'
        >
            <div className='grid gap-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.7fr)] lg:gap-24'>
                <h2 id='about-title' data-reveal className='flex items-start gap-6'>
                    <span className='pl-[0.3em] font-heading text-[2.4rem] font-medium leading-none tracking-[0.3em] text-champagne md:text-[2.8rem]'>
                        AERIVE
                    </span>
                    <span className='mt-1 text-[10px] uppercase leading-relaxed tracking-[0.26em] text-ivory/70'>
                        Private
                        <br />
                        aviation
                    </span>
                </h2>

                <div className='grid gap-x-14 gap-y-16 sm:grid-cols-2'>
                    {PILLARS.map((pillar) => (
                        <article key={pillar.title} data-reveal>
                            <h3 className='font-heading text-[2rem] font-normal leading-[1.05] text-ivory md:text-[2.3rem]'>
                                {pillar.title}
                            </h3>
                            <span className='my-6 block h-px w-10 bg-champagne/80' />
                            <p className='max-w-sm text-[14px] leading-[1.75] text-ivory/80'>
                                {pillar.text}
                            </p>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default Ascent
